// src/lib/scores/official-score.ts
//
// Pure validation + types for a student-entered OFFICIAL College Board score
// (roadmap 2.3). DB-free so the API route and the client form can share one
// source of truth and it's trivially unit-testable.

export type ExamType = 'SAT' | 'PSAT'

/** Scaled-score ranges per exam. Real scores are always multiples of 10. */
export const EXAM_RANGES: Record<
  ExamType,
  { totalMin: number; totalMax: number; secMin: number; secMax: number }
> = {
  SAT: { totalMin: 400, totalMax: 1600, secMin: 200, secMax: 800 },
  PSAT: { totalMin: 320, totalMax: 1520, secMin: 160, secMax: 760 },
}

export interface OfficialScoreInput {
  total: number
  rw?: number | null
  math?: number | null
  examType?: string
  /** ISO date string (YYYY-MM-DD) or full ISO; optional. */
  testDate?: string | null
}

export interface OfficialScore {
  total: number
  rw: number | null
  math: number | null
  examType: ExamType
  testDate: Date | null
}

export type ValidationResult = { ok: true; value: OfficialScore } | { ok: false; error: string }

function isExamType(v: unknown): v is ExamType {
  return v === 'SAT' || v === 'PSAT'
}

/** A scaled score must be an integer, in range, and a multiple of 10. */
function validScaled(n: number, min: number, max: number): boolean {
  return Number.isInteger(n) && n >= min && n <= max && n % 10 === 0
}

/**
 * Validate + normalize a raw official-score entry. Returns the typed score on
 * success or a human-readable error. Rules:
 *  - examType ∈ {SAT, PSAT} (defaults SAT)
 *  - total in the exam's range, multiple of 10
 *  - rw/math optional; if present, in the section range, multiple of 10
 *  - if BOTH sections given, total must equal rw + math (the SAT identity)
 *  - testDate optional; must parse and not be in the future
 */
export function validateOfficialScore(input: OfficialScoreInput): ValidationResult {
  const examType: ExamType = isExamType(input.examType) ? input.examType : 'SAT'
  const r = EXAM_RANGES[examType]

  if (typeof input.total !== 'number' || !validScaled(input.total, r.totalMin, r.totalMax)) {
    return {
      ok: false,
      error: `Total must be a multiple of 10 between ${r.totalMin} and ${r.totalMax}.`,
    }
  }

  const rw = input.rw == null ? null : input.rw
  const math = input.math == null ? null : input.math

  if (rw != null && !validScaled(rw, r.secMin, r.secMax)) {
    return {
      ok: false,
      error: `Reading & Writing must be a multiple of 10 between ${r.secMin} and ${r.secMax}.`,
    }
  }
  if (math != null && !validScaled(math, r.secMin, r.secMax)) {
    return {
      ok: false,
      error: `Math must be a multiple of 10 between ${r.secMin} and ${r.secMax}.`,
    }
  }
  if (rw != null && math != null && rw + math !== input.total) {
    return { ok: false, error: 'Reading & Writing + Math must equal the total score.' }
  }

  let testDate: Date | null = null
  if (input.testDate != null && input.testDate !== '') {
    const d = new Date(input.testDate)
    if (Number.isNaN(d.getTime())) return { ok: false, error: 'Test date is not a valid date.' }
    // Allow today; reject the future (a small clock-skew grace of 1 day).
    if (d.getTime() > Date.now() + 24 * 60 * 60 * 1000) {
      return { ok: false, error: 'Test date cannot be in the future.' }
    }
    testDate = d
  }

  return { ok: true, value: { total: input.total, rw, math, examType, testDate } }
}
